// components/sidebar/index.jsx - Beautiful Enterprise Theme
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  Stack,
  Tooltip,
  useTheme,
  alpha,
} from "@mui/material";
import { ChevronLeft as ChevronLeftIcon } from "@mui/icons-material";
import { SidebarLinks } from "./components/Links";
import { getSecureLocalItem } from "utils/secureSessionStorage";
import SidebarLogo from "assets/sidebar/sidebar-top-files.svg";

const Sidebar = ({ open, collapsed, onClose, onToggleCollapse, isMobile }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const role = getSecureLocalItem("role") || "admin";
  const sidebarRef = useRef(null);

  const isExpanded = open && !collapsed;
  const sidebarWidth = collapsed ? 80 : 280;

  // Handle click outside on mobile
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isMobile &&
        open &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target)
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMobile, open, onClose]);

  // backgrounds
  const sidebarGradient =
    theme.palette.mode === "dark"
      ? "linear-gradient(180deg, #0f172a 0%, #1e1b2e 50%, #0f172a 100%)"
      : "linear-gradient(180deg, #fef3c7 0%, #fffbeb 50%, #fef3c7 100%)";
  // Glass morphism effect for sidebar
  const glassEffect =
    theme.palette.mode === "dark"
      ? {
          backgroundColor: alpha("#0f172a", 0.8),
          backdropFilter: "blur(12px)",
        }
      : {
          backgroundColor: alpha("#fef3c7", 0.8),
          backdropFilter: "blur(12px)",
        };

  // Sidebar content
  const sidebarContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        background: sidebarGradient,
        borderRight: `1px solid ${
          theme.palette.mode === "dark"
            ? alpha("#f59e0b", 0.15)
            : alpha("#f59e0b", 0.12)
        }`,
        boxShadow:
          theme.palette.mode === "dark"
            ? "0 0 40px rgba(0, 0, 0, 0.3)"
            : "0 0 40px rgba(0, 0, 0, 0.05)",
        ...glassEffect,
      }}
    >
      {/* Animated Header with SVG Logo */}
      <Box
        sx={{
          position: "relative",
          borderBottom: `1px solid ${
            theme.palette.mode === "dark"
              ? alpha("#f59e0b", 0.1)
              : alpha("#f59e0b", 0.08)
          }`,
          px: 2.5,
          py: 3,
          overflow: "hidden",
        }}
      >
        {/* Decorative gradient line at top */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "6px",
            background: "linear-gradient(90deg, #f59e0b, #fbbf24, #cec107)",
            opacity: 0.6,
          }}
        />

        <AnimatePresence mode="wait">
          {isExpanded ? (
            <motion.div
              key="expanded"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.2 }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  {/* Animated SVG Logo */}
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    transition={{ type: "tween", duration: 0.4 }}
                  >
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Box
                        component="img"
                        src={SidebarLogo}
                        alt="EBS OMS Logo"
                        sx={{
                          width: 40,
                          height: 40,
                          objectFit: "contain",
                        }}
                      />
                    </Box>
                  </motion.div>

                  <Box>
                    <Typography
                      variant="h6"
                      component="h1"
                      sx={{
                        fontWeight: 800,
                        letterSpacing: "-0.025em",
                        background: "linear-gradient(135deg, #f59e0b, #fbbf24)",
                        backgroundClip: "text",
                        WebkitBackgroundClip: "text",
                        color: "transparent",
                      }}
                    >
                      EBS OMS
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color:
                          theme.palette.mode === "dark"
                            ? alpha("#b4c5dd", 0.8)
                            : alpha("#565c68", 0.8),
                        fontSize: "0.75rem",
                        letterSpacing: "0.35px",
                        fontWeight: 500,
                      }}
                    >
                      OFFICE MANAGEMENT
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </motion.div>
          ) : (
            <motion.div
              key="collapsed"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2 }}
              style={{ display: "flex", justifyContent: "center" }}
            >
              <Tooltip title="Expand sidebar" placement="right" arrow>
                <motion.div
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Box
                    onClick={() => !isMobile && onToggleCollapse()}
                    sx={{
                      width: 48,
                      height: 48,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <Box
                      component="img"
                      src={SidebarLogo}
                      alt="EBS OMS Logo"
                      sx={{
                        width: 32,
                        height: 32,
                        objectFit: "contain",
                      }}
                    />
                  </Box>
                </motion.div>
              </Tooltip>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>

      {/* Navigation Links with smooth scrollbar */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          px: 1.5,
          py: 3,
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? alpha("#1e293b", 0.5)
                : alpha("#f3f4f6", 0.5),
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: alpha("#f59e0b", 0.4),
            borderRadius: "4px",
            transition: "background-color 0.2s",
            "&:hover": {
              backgroundColor: "#f59e0b",
            },
          },
        }}
      >
        <SidebarLinks
          collapsed={collapsed}
          location={location}
          onItemClick={onClose}
        />
      </Box>

      {/* Decorative bottom gradient */}
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "60px",
          background: `linear-gradient(180deg, transparent 0%, ${
            theme.palette.mode === "dark"
              ? alpha("#0f172a", 0.9)
              : alpha("#ffffff", 0.9)
          } 100%)`,
          pointerEvents: "none",
        }}
      />
    </Box>
  );

  // Mobile drawer with animation
  if (isMobile) {
    return (
      <Drawer
        anchor="left"
        open={open}
        onClose={onClose}
        sx={{
          "& .MuiDrawer-paper": {
            width: sidebarWidth,
            boxSizing: "border-box",
            border: "none",
            backgroundColor: "transparent",
            boxShadow: "none",
          },
        }}
      >
        {sidebarContent}
      </Drawer>
    );
  }

  // Desktop sidebar with smooth framer-motion animation
  return (
    <motion.aside
      ref={sidebarRef}
      initial={false}
      animate={{
        width: open ? sidebarWidth : 0,
        x: open ? 0 : -sidebarWidth,
        opacity: open ? 1 : 0,
      }}
      transition={{
        width: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
        x: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
        opacity: { duration: 0.2 },
      }}
      style={{
        position: "fixed",
        left: 0,
        top: 0,
        zIndex: 50,
        height: "100vh",
        overflow: "hidden",
      }}
    >
      {sidebarContent}
    </motion.aside>
  );
};

export default Sidebar;
